/**
 * Utilidad para validar campos obligatorios en cada paso del formulario
 */

/**
 * Valida los campos obligatorios del paso 1 (Datos personas)
 * @param {Object} cartransfer - Datos del cartransfer
 * @returns {Object} - { isValid: boolean, missingFields: string[], message: string }
 */
const validatePerson = (person, label, missingFields) => {
  if (!person?.name) missingFields.push(`Nombre de ${label}`);
  if (!person?.firstLastName) missingFields.push(`Apellido paterno de ${label}`);
  if (!person?.secondLastName) missingFields.push(`Apellido materno de ${label}`);
  if (!person?.civilStatus) missingFields.push(`Estado civil de ${label}`);
  if (!person?.phone) missingFields.push(`Teléfono de ${label}`);
  if (!person?.address) missingFields.push(`Dirección de ${label}`);
};

export const validateStep1 = (cartransfer) => {
  const missingFields = [];

  // Soporta arrays buyers[]/sellers[] o singular buyer/seller
  const sellers = cartransfer.sellers?.length > 0 ? cartransfer.sellers : (cartransfer.seller ? [cartransfer.seller] : []);
  const buyers = cartransfer.buyers?.length > 0 ? cartransfer.buyers : (cartransfer.buyer ? [cartransfer.buyer] : []);

  if (sellers.length === 0) {
    missingFields.push("Al menos un vendedor");
  } else {
    sellers.forEach((s, i) => validatePerson(s, sellers.length > 1 ? `vendedor #${i + 1}` : "vendedor", missingFields));
  }

  if (buyers.length === 0) {
    missingFields.push("Al menos un comprador");
  } else {
    buyers.forEach((b, i) => validatePerson(b, buyers.length > 1 ? `comprador #${i + 1}` : "comprador", missingFields));
  }

  const isValid = missingFields.length === 0;
  const message = isValid
    ? "Paso 1 completado correctamente"
    : `Faltan los siguientes campos obligatorios: ${missingFields.join(", ")}`;

  return { isValid, missingFields, message };
};

/**
 * Valida los campos obligatorios del paso 2 (Documentos)
 * @param {Object} cartransfer - Datos del cartransfer
 * @returns {Object} - { isValid: boolean, missingFields: string[], message: string }
 */
export const validateStep2 = (cartransfer) => {
  const missingFields = [];
  
  // Validar datos básicos del vehículo (estos son obligatorios)
  if (!cartransfer.background?.type) {
    missingFields.push("Tipo de vehículo");
  }
  if (!cartransfer.background?.make) {
    missingFields.push("Marca del vehículo");
  }
  if (!cartransfer.background?.model) {
    missingFields.push("Modelo del vehículo");
  }
  if (!cartransfer.background?.year) {
    missingFields.push("Año del vehículo");
  }
  if (!cartransfer.background?.inscription) {
    missingFields.push("Patente del vehículo");
  }
  
  // El folio y código de verificación no son obligatorios para avanzar
  // Solo se requieren si se quiere obtener información automática del permiso
  
  const isValid = missingFields.length === 0;
  const message = isValid 
    ? "Paso 2 completado correctamente"
    : `Faltan los siguientes campos obligatorios: ${missingFields.join(", ")}`;
  
  return { isValid, missingFields, message };
};

/**
 * Valida los campos obligatorios del paso 3 (Contrato)
 * @param {Object} cartransfer - Datos del cartransfer
 * @returns {Object} - { isValid: boolean, missingFields: string[], message: string }
 */
export const validateStep3 = (cartransfer) => {
  const missingFields = [];
  
  // Debug logging
  console.log('🔍 validateStep3 - cartransfer:', cartransfer);
  console.log('🔍 validateStep3 - contract:', cartransfer.contract);
  console.log('🔍 validateStep3 - paymentMethod:', cartransfer.contract?.paymentMethod);
  console.log('🔍 validateStep3 - price:', cartransfer.contract?.price);
  
  // Validar datos del contrato
  if (!cartransfer.contract?.price || cartransfer.contract.price <= 0) {
    missingFields.push("Precio de venta");
  }
  if (!cartransfer.contract?.paymentMethod || cartransfer.contract.paymentMethod.trim() === "") {
    missingFields.push("Metodo de pago");
  }
  // Fechas del contrato se rellenan automáticamente luego; no bloquear paso
  
  // Datos del notario se rellenan automáticamente luego; no bloquear paso
  
  console.log('🔍 validateStep3 - missingFields:', missingFields);
  
  const isValid = missingFields.length === 0;
  const message = isValid 
    ? "Paso 3 completado correctamente"
    : `Faltan los siguientes campos obligatorios: ${missingFields.join(", ")}`;
  
  return { isValid, missingFields, message };
};

/**
 * Valida un paso específico
 * @param {number} stepNumber - Número del paso (1, 2, o 3)
 * @param {Object} cartransfer - Datos del cartransfer
 * @returns {Object} - { isValid: boolean, missingFields: string[], message: string }
 */
export const validateStep = (stepNumber, cartransfer) => {
  switch (stepNumber) {
    case 1:
      return validateStep1(cartransfer);
    case 2:
      return validateStep2(cartransfer);
    case 3:
      return validateStep3(cartransfer);
    default:
      return { isValid: true, missingFields: [], message: "Paso válido" };
  }
};

/**
 * Valida si se puede avanzar al siguiente paso
 * @param {number} currentStep - Paso actual
 * @param {Object} cartransfer - Datos del cartransfer
 * @returns {Object} - { canProceed: boolean, validation: Object }
 */
export const canProceedToNextStep = (currentStep, cartransfer) => {
  const validation = validateStep(currentStep, cartransfer);
  return {
    canProceed: validation.isValid,
    validation
  };
};
